import { Card, CardBody, Typography, Chip } from '@material-tailwind/react';
import { AutoCompleteSelectUI, ButtonUI, CheckboxUI, DateTimePickerUI, FormLabelUI } from '../Interface';
import { ColorPicker } from '../../config/ColorPicker';
import dayjs from 'dayjs';
import MessageIcon from '@mui/icons-material/Message';
import TaskListTodoTabs from './TaskListTodoTabs';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Badge } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import taskListService from '../../service/taskList.service';
import useParam from '../../context/ParamContext';
import appService from '../../service/app.service';

export default function TaskListTodoCard({
  setTaskListTodo,
  task,
  autoCompleteOptions,
  user,
  autoCompleteData,
  handleOpenCommentDialog,
}) {
  const { params } = useParam();
  const [expanded, setExpanded] = useState(false);

  const reportAttachments = task.taskListReport.flatMap((rep) => rep.taskListReportAttachment || []);

  const normalizeOldFiles = (files) =>
    (files ?? []).map((f) => ({
      ...f,
      id: f.id,
      deletable: false,
    }));

  const [editedTask, setEditedTask] = useState({
    ...task,
    description: '',
    attachment: [...normalizeOldFiles(task.taskListAttachment), ...normalizeOldFiles(reportAttachments)],
  });

  const isRequiredDescAndAttachment = params.TASKLIST_TODO_REQUIRED_DESC_ATTACHMENT === 'Y';
  const colorCardBackground = ColorPicker.find((colbg) => colbg.colorName === task.status?.color);

  const getNewAttachments = () => editedTask.attachment.filter((att) => att && att.isNew === true);

  const handleChange = (field, value) => {
    setEditedTask((prev) => ({ ...prev, [field]: value }));
  };

  const handleInsert = () => {
    const foundedStatus = autoCompleteData?.status?.statusItems?.find((item) => item.itemId === editedTask.statusId);

    const { attachment: _omitAttachment, ...rest } = editedTask;

    const values = {
      ...rest,
      createdBy: user.userId,
      executionTime: !editedTask.executionTime ? dayjs() : editedTask.executionTime,
      preparedness: foundedStatus?.preparedness ? parseFloat(foundedStatus?.preparedness) : editedTask.preparedness,
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(values));

    const newAtts = getNewAttachments();
    for (const item of newAtts) {
      const file =
        item instanceof File
          ? item
          : item?.file instanceof File
            ? item.file
            : item?.blob instanceof Blob && item?.name
              ? new File([item.blob], item.name, { type: item.blob.type })
              : null;
      if (file) formData.append('files', file);
    }

    taskListService.createTaskListReport(formData).then((data) => {
      setTaskListTodo((prevRows) => prevRows.map((row) => (row.id === data.id ? { ...row, ...data } : row)));

      setEditedTask({ ...data, description: '', attachment: task.taskListAttachment });
      setExpanded(false);
    });
  };

  const hasDescription = editedTask.description?.trim?.().length > 0;
  const hasAttachment = getNewAttachments().length > 0;

  const handleUpdateReadAt = () => {
    const findedItem = task.messages?.find((msg) => msg.lineId === task.id);
    appService.updateReadAt(findedItem.id).then(() => {
      setTaskListTodo((prevTasks) =>
        prevTasks.map((t) => {
          if (t.id !== task.id) return t;
          return {
            ...t,
            messages: t.messages?.filter((msg) => msg.id !== findedItem.id),
          };
        })
      );
    });
  };

  return (
    <Card
      className="w-[450px]"
      style={{
        background: colorCardBackground?.colorBgCode,
        border: `4px solid ${task?.responsiblePerson?.color || 'transparent'}`,
      }}
    >
      <CardBody>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h5" color="blue-gray">
              {task.name}
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                variant="ghost"
                value={`${task.preparedness ?? 0} %`}
                className="rounded-full"
                style={{
                  background: task.preparedness < 100 ? 'rgba(254, 215, 170, 0.6)' : 'rgba(187, 247, 208, 1)',
                  color: task.preparedness < 100 ? 'rgba(249, 115, 22, 1)' : 'rgba(22, 163, 74, 1)',
                }}
                size="sm"
              />
              <Badge
                badgeContent={task.commentCount ?? 0}
                showZero
                color="secondary"
                sx={{
                  color: 'grey',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  handleOpenCommentDialog(task.id);
                }}
              >
                <MessageIcon />
              </Badge>
            </Box>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              {task?.messages?.some((msg) => msg.addressedTo === user.userId) && (
                <CheckboxUI onChange={handleUpdateReadAt} label="Elolvastam" />
              )}
            </Box>

            <Typography variant="h6">{task?.userGroup?.name}</Typography>
          </Box>
        </Box>
        <div className="grid grid-cols-2 gap-5 my-3">
          <div className="col-span-2">
            <DateTimePickerUI
              label={<FormLabelUI text="Kivitelezés ideje" />}
              value={editedTask.executionTime ? dayjs(editedTask.executionTime) : dayjs()}
              onChange={(date) => handleChange('executionTime', date.toISOString())}
              disableClearable
            />
          </div>
          <div className="col-span-2">
            <AutoCompleteSelectUI
              label={<FormLabelUI text="Projekt neve" />}
              dataset={autoCompleteOptions.project}
              selectedValue={editedTask.lineId}
              selectedLabelValue={
                autoCompleteData?.project?.find((project) => project.projectId === editedTask.lineId)?.projectName
              }
              disabled
            />
          </div>
          <div>
            <AutoCompleteSelectUI
              label={<FormLabelUI text="Dolgozó" />}
              dataset={autoCompleteOptions.user}
              selectedValue={task.responsiblePersonId}
              selectedLabelValue={task.responsiblePerson?.nickName}
              disabled
            />
          </div>
          <div>
            <AutoCompleteSelectUI
              label={<FormLabelUI text="Státusz" />}
              selectedValue={editedTask.statusId}
              selectedLabelValue={editedTask.status?.itemName}
              dataset={autoCompleteOptions.status}
              onChange={(_, newVal) => {
                handleChange('statusId', newVal.value);
                setExpanded(true);
              }}
              disableClearable
            />
          </div>
        </div>
        {task.description && (
          <Box mb={1}>
            <FormLabelUI text="Megjegyzés" />
            <Box
              sx={{
                maxHeight: 120,
                overflowY: 'auto',
              }}
              className="custom-scrollbar"
            >
              <Typography color="blue-gray" className="whitespace-pre-wrap break-words">
                {task.description}
              </Typography>
            </Box>
          </Box>
        )}
        <Accordion sx={{ width: '100%', background: 'none', boxShadow: 'none' }} expanded={expanded}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={() => setExpanded(!expanded)}>
            <Typography component="span">További információk</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <TaskListTodoTabs editedTask={editedTask} handleChange={handleChange} setEditedTask={setEditedTask} />
            <div className="text-center mb-2">
              <ButtonUI
                text="Rögzítés"
                className="bg-success"
                onClick={() => handleInsert()}
                disabled={isRequiredDescAndAttachment ? !(hasDescription && hasAttachment) : false}
              />
            </div>
          </AccordionDetails>
        </Accordion>
      </CardBody>
    </Card>
  );
}
